import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Progress, List, Empty, Spin, Typography, DatePicker, Table, Tag, Tabs, Button } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { isEmpty } from 'underscore';
import dayjs, { Dayjs } from 'dayjs';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getSchoolIndicatorCategories, getCategoryIndicatorsReport } from '../../core/indicator/indicatorApi';
import SchoolCompare from '../IndicatorStatisticsComponents/SchoolCompare';
import SchoolIndicatorsImport from '../IndicatorStatisticsComponents/SchoolIndicatorsImport';
import './indicatorStatistics.scss';

const { Title, Text } = Typography;

// Helper function to safely format numeric values
const formatNumericValue = (value: any, decimals = 1): string => {
    if (value === undefined || value === null) return '-';

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(numValue)) return '-';

    return numValue.toFixed(decimals);
};

const IndicatorStatistics: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

    const {
        schoolIndicatorCategories,
        categoryReport,
        isFetching,
        isFetchingCategoryReport,
    } = useSelector((state: CombinedStates) => state.indicator);

    const getDefaultDate = (): Dayjs => {
        const now = dayjs();
        const schoolYearStart = now.month() < 8 ? now.year() - 1 : now.year();
        return dayjs(`${schoolYearStart}-09-01`);
    };

    useEffect(() => {
        setSelectedDate(getDefaultDate());
    }, []);

    useEffect(() => {
        if (isEmpty(schoolIndicatorCategories)) {
            dispatch(getSchoolIndicatorCategories({}));
        }
    }, [dispatch, schoolIndicatorCategories]);

    useEffect(() => {
        if (selectedCategory && selectedDate) {
            dispatch(getCategoryIndicatorsReport({
                category_id: selectedCategory,
                date: selectedDate.format('YYYY-MM-DD'),
            }));
        }
    }, [dispatch, selectedCategory, selectedDate]);

    const handleCategorySelect = (categoryId: number) => {
        setSelectedCategory(categoryId);
    };

    const handleDateChange = (date: Dayjs | null) => {
        setSelectedDate(date);
    };

    const renderCategoryStatistics = () => {
        if (!selectedCategory) {
            return (
                <div className="category-statistics-placeholder">
                    <Empty
                        description="Pasirinkite kategoriją, kad pamatytumėte jos statistiką"
                        image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            );
        }

        if (!categoryReport) {
            return (
                <div className="category-statistics-placeholder">
                    <Spin size="large" />
                    <Text>Kraunama statistika...</Text>
                </div>
            );
        }

        if (isFetchingCategoryReport) {
            return (
                <div className="category-statistics-placeholder">
                    <Spin size="large" />
                    <Text>Kraunama statistika...</Text>
                </div>
            );
        }

        const { statistics, indicators } = categoryReport;

        return (
            <div className="category-statistics">
                <Title level={4}>{categoryReport.category.title} - Statistika</Title>

                {/* Overall Statistics */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card className="stat-card">
                            <Statistic
                                title="Bendras rodiklių skaičius"
                                value={statistics.total_indicators}
                                suffix="vnt" />
                            <Progress percent={100} size="small" />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card className="stat-card">
                            <Statistic
                                title="Rodikliai su duomenimis"
                                value={statistics.indicators_with_data}
                                suffix="vnt" />
                            <Progress
                                percent={statistics.total_indicators > 0 ? Number(((statistics.indicators_with_data / statistics.total_indicators) * 100).toFixed(2)) : 0}
                                size="small"
                                status="active" />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card className="stat-card">
                            <Statistic
                                title="Duomenų užpildymas"
                                value={statistics.data_completion_rate}
                                suffix="%" />
                            <Progress percent={statistics.data_completion_rate} size="small" />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Card className="stat-card">
                            <Statistic
                                title="Analizuojamos mokyklos"
                                value={statistics.total_schools_analyzed}
                                suffix="vnt" />
                            <Progress
                                percent={statistics.total_schools_analyzed > 0 ? (statistics.schools_with_data / statistics.total_schools_analyzed) * 100 : 0}
                                size="small"
                                status="exception" />
                        </Card>
                    </Col>
                </Row>

                <div className="category-details">
                    <h4>Kategorijos aprašymas:</h4>
                    <p>{categoryReport.category.description || 'Aprašymas nepateiktas'}</p>

                    <h4>Analizės data:</h4>
                    <p>
                        {selectedDate ? selectedDate.format('YYYY-MM-DD') : '-'}
                    </p>

                    <h4>Detalesnė analizė:</h4>
                    <ul>
                        <li>Bendras rodiklių skaičius: {statistics.total_indicators}</li>
                        <li>Rodikliai su duomenimis: {statistics.indicators_with_data}</li>
                        <li>Analizuojamos mokyklos: {statistics.total_schools_analyzed}</li>
                        <li>Mokyklos su duomenimis: {statistics.schools_with_data}</li>
                        <li>Duomenų užpildymo procentas: {statistics.data_completion_rate}%</li>
                    </ul>
                </div>

                {/* Indicators Table */}
                <Card title="Rodiklių detalės" className="indicators-table-card">
                    <Table
                        dataSource={indicators}
                        rowKey={(record) => record.indicator.id}
                        pagination={false}
                        size="small"
                        columns={[
                            {
                                title: 'Rodiklis',
                                dataIndex: ['indicator', 'title'],
                                key: 'title',
                                render: (text, record) => (
                                    <div>
                                        <Text strong>{text} (R{record.indicator.id})</Text>
                                        {record.indicator.description && (
                                            <div>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    {record.indicator.description}
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                ),
                            },
                            {
                                title: 'Vienetas',
                                dataIndex: ['indicator', 'unit'],
                                key: 'unit',
                                width: 80,
                                render: (unit) => unit || '-',
                            },
                            {
                                title: 'Mokyklos su duomenimis',
                                dataIndex: ['statistics', 'schools_with_data'],
                                key: 'schools_with_data',
                                width: 120,
                                render: (schoolsWithData, record) => (
                                    <div>
                                        <Text>{schoolsWithData}</Text>
                                        <div>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                iš {record.statistics.total_schools}
                                            </Text>
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                title: 'Vidurkis',
                                dataIndex: ['statistics', 'average'],
                                key: 'average',
                                width: 100,
                                render: (average, record) => {
                                    const formattedValue = formatNumericValue(average);
                                    if (formattedValue !== '-') {
                                        return (
                                            <div>
                                                <Text strong>{formattedValue}</Text>
                                                {record.indicator.unit && (
                                                    <div>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            {record.indicator.unit}
                                                        </Text>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    return '-';
                                },
                            },
                            {
                                title: 'Min/Max',
                                key: 'min_max',
                                width: 100,
                                render: (_, record) => {
                                    const { min, max } = record.statistics;
                                    const formattedMin = formatNumericValue(min);
                                    const formattedMax = formatNumericValue(max);

                                    if (formattedMin !== '-' && formattedMax !== '-') {
                                        return (
                                            <div>
                                                <Text>{formattedMin} / {formattedMax}</Text>
                                                {record.indicator.unit && (
                                                    <div>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            {record.indicator.unit}
                                                        </Text>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    return '-';
                                },
                            },
                            {
                                title: 'Suma',
                                key: 'sum',
                                width: 100,
                                render: (_, record) => {
                                    const { sum } = record.statistics;
                                    const formattedSum = formatNumericValue(sum);
                                    if (formattedSum !== '-') {
                                        return (
                                            <div>
                                                <Text>{formattedSum}</Text>
                                                {record.indicator.unit && (
                                                    <div>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            {record.indicator.unit}
                                                        </Text>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    return '-';
                                },
                            },
                            {
                                title: 'Mediana',
                                key: 'median',
                                width: 100,
                                render: (_, record) => {
                                    const { median } = record.statistics;
                                    const formattedMedian = formatNumericValue(median);
                                    if (formattedMedian !== '-') {
                                        return (
                                            <div>
                                                <Text>{formattedMedian}</Text>
                                                {record.indicator.unit && (
                                                    <div>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            {record.indicator.unit}
                                                        </Text>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    return '-';
                                },
                            },
                            {
                                title: 'Statusas',
                                key: 'status',
                                width: 100,
                                render: (_, record) => {
                                    const completionRate = record.statistics.total_schools > 0
                                        ? (record.statistics.schools_with_data / record.statistics.total_schools) * 100
                                        : 0;

                                    let color = 'red';
                                    let text = 'Nepilnas';

                                    if (completionRate >= 100) {
                                        color = 'green';
                                        text = 'Pilnas';
                                    } else if (completionRate >= 50) {
                                        color = 'orange';
                                        text = 'Dalinis';
                                    }

                                    return <Tag color={color}>{text}</Tag>;
                                },
                            },
                            {
                                title: '',
                                key: 'actions',
                                width: 160,
                                render: (_, record) => {
                                    const effectiveDateFrom = selectedDate
                                        ? selectedDate.format('YYYY-MM-DD')
                                        : getDefaultDate().format('YYYY-MM-DD');
                                    const statusUrl = `/rodiklio-mokyklu-statusas?indicator_id=${record.indicator.id}&date_from=${effectiveDateFrom}`;

                                    return (
                                        <div>
                                            <Link to={statusUrl} target="_blank">
                                                <Button size="small" icon={<EyeOutlined />}>
                                                    Tikrinti suvedimą
                                                </Button>
                                            </Link>
                                            <div>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                                    Laikotarpiui: {effectiveDateFrom}
                                                </Text>
                                            </div>
                                        </div>
                                    );
                                },
                            },
                        ]} />
                </Card>
            </div>
        );
    };

    const renderStatisticsTab = () => {
        if (isFetching && !categoryReport) {
            return (
                <div className="loading-container">
                    <Spin size="large" />
                    <Text>Kraunama...</Text>
                </div>
            );
        }

        if (!schoolIndicatorCategories || schoolIndicatorCategories.length === 0) {
            return (
                <Empty
                    description="Rodiklių kategorijų nėra"
                    image={Empty.PRESENTED_IMAGE_SIMPLE} />
            );
        }

        return (
            <>
                {/* Date Picker */}
                <div className="date-range-section">
                    <Row gutter={16} align="middle">
                        <Col xs={24} sm={6}>
                            <Text strong>Data:</Text>
                        </Col>
                        <Col xs={24} sm={18}>
                            <DatePicker
                                value={selectedDate}
                                onChange={handleDateChange}
                                format="YYYY-MM-DD"
                                placeholder="Pasirinkite datą"
                                allowClear={false} />
                        </Col>
                    </Row>
                </div>

                <Row gutter={24}>
                    {/* Left side - Categories */}
                    <Col xs={24} md={6} lg={6}>
                        <div className="categories-section">
                            <Title level={4}>Rodiklių kategorijos</Title>
                            <List
                                className="categories-list"
                                dataSource={schoolIndicatorCategories}
                                renderItem={(category) => (
                                    <List.Item
                                        className={`category-item ${selectedCategory === category.id ? 'selected' : ''}`}
                                        onClick={() => handleCategorySelect(category.id)}>
                                        <div className="category-content">
                                            <Title level={5} className="category-title">
                                                {category.title}
                                            </Title>
                                            {category.description && (
                                                <Text type="secondary" className="category-description">
                                                    {category.description}
                                                </Text>
                                            )}
                                        </div>
                                    </List.Item>
                                )} />
                        </div>
                    </Col>

                    {/* Right side - Statistics */}
                    <Col xs={24} md={18} lg={18}>
                        <div className="statistics-section">
                            {renderCategoryStatistics()}
                        </div>
                    </Col>
                </Row>
            </>
        );
    };

    const renderCompareTab = () => (
        <SchoolCompare />
    );

    const tabItems = [
        {
            key: 'statistics',
            label: 'Rodiklių statistika',
            children: renderStatisticsTab(),
        },
        {
            key: 'compare',
            label: 'Mokyklų palyginimas',
            children: renderCompareTab(),
        },
    ];

    return (
        <div className="indicator-statistics">
            <Card className="statistics-card">
                <Tabs
                    defaultActiveKey="statistics"
                    items={tabItems}
                    className="statistics-tabs" />
            </Card>
        </div>
    );
};

export default IndicatorStatistics;
